/**
 * Meeri API client. One module wraps every /api/v1 endpoint we use.
 *
 * Auth strategy: cookie-based (HttpOnly), so each call uses `credentials: 'include'`.
 * In dev we point BASE at the EC2 host via Vite proxy; in prod the frontend is
 * served from the same origin as the API so we can use a relative URL.
 */

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown, message?: string) {
    super(message ?? `${status}`);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData must set its own multipart boundary — never force JSON on it.
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const r = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!r.ok) {
    let detail: unknown;
    try {
      detail = await r.json();
    } catch {
      detail = await r.text();
    }
    throw new ApiError(r.status, detail);
  }
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await r.json()) as T;
  return (await r.text()) as unknown as T;
}

// --- types (mirror schemas/*) ---

export type Mode = 'sim' | 'live';

/**
 * Response from POST /api/v1/research/technical-zones (AE-010, AE-011).
 *
 * Discriminated union: when bars are scarce or unavailable, the server
 * returns `{ available: false, reason }` rather than fabricating a
 * zone. Frontend MUST handle both shapes — the UI shows the reason
 * verbatim and does NOT plot a fake band.
 */
export type TechnicalZonesResponse =
  | {
      available: false;
      reason: string;
      symbol: string;
      bars_returned?: number;
    }
  | {
      available: true;
      symbol: string;
      method: string;
      lookback: number;
      support: number;
      resistance: number;
      current_price: number;
      entry_zone: 'buy_zone' | 'neutral' | 'sell_zone';
      channel_position_pct: number;
      confidence: number;
      as_of: string | null;
    };

export interface UserPublic {
  id: number;
  email: string;
  display_name: string | null;
  is_admin: boolean;
}

export interface MeResponse {
  user: UserPublic;
  mode: Mode;
  schwab_connected: boolean;
}

export interface Quote {
  symbol: string;
  last: number;
  bid: number | null;
  ask: number | null;
  change_pct: number | null;
  timestamp: string | null;
  source: string;
}

export interface QuoteBatch {
  quotes: Record<string, Quote>;
}

export interface Bar {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface HistoryResponse {
  symbol: string;
  period: string;
  interval: string;
  bars: Bar[];
  source: string;
}

export type PositionMode = 'sim' | 'live';

export interface PositionPublic {
  symbol: string;
  quantity: number;
  avg_price: number;
  market_price: number;
  market_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number | null;
  // v0.5.5 dual-ledger: tag for the "live / paper" pill in the positions
  // table. Defaults to 'sim' for older callers that haven't migrated.
  mode?: PositionMode;
}

export interface AccountPublic {
  cash: number;
  equity: number;
  buying_power: number;
  positions_count: number;
  source: string;
  account_id: string | null;
}

export interface LedgerSummary {
  nav: number | null;
  cash: number | null;
  day_change_pct: number | null;
  positions_count: number;
  source: 'schwab' | 'sim';
  available: boolean;
  error: string | null;
}

export interface DualLedgerAccount {
  combined_nav: number | null;
  sim: LedgerSummary | null;
  live: LedgerSummary | null;
}

export interface PnLResponse {
  period: string;
  starting_equity: number;
  ending_equity: number;
  total_return_pct: number;
  points: { ts: string; equity: number }[];
  source: string;
}

// v0.5.8 AE-104: daily "you vs S&P" alpha series
export interface AlphaHistoryPoint {
  day: string;
  portfolio_value: number;
  sp500_value: number;
  alpha_pct: number;
}

export interface AlphaHistoryResponse {
  available: boolean;
  points: AlphaHistoryPoint[];
}

export interface OrderPublic {
  id: string | null;
  client_order_id: string | null;
  symbol: string;
  side: string;
  quantity: number;
  fill_quantity: number | null;
  fill_price: number | null;
  order_type: string;
  limit_price: number | null;
  /** pending_approval | approved | filled | partial | canceled | rejected | expired */
  status: string;
  mode: string;
  created_at: string;
  ticket_id: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  rejected_at?: string | null;
  rejected_reason?: string | null;
  approval_expires_at?: string | null;
}

export interface PlaceOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  order_type?: 'market' | 'limit' | 'stop';
  limit_price?: number | null;
  stop_loss_pct?: number | null;
  time_in_force?: 'day' | 'gtc';
  client_order_id?: string | null;
}

export interface OrderPreview {
  estimated_cost: number;
  estimated_fill_price: number;
  buying_power_after: number | null;
  compliance_approved: boolean;
  compliance_violations: string[];
  risk_approved: boolean;
  risk_violations: string[];
  mode: string;
  requires_live_ack: boolean;
}

export interface WatchlistPublic {
  id: number;
  name: string;
  is_default: boolean;
  items: { symbol: string; sort_order: number; note: string | null }[];
  created_at: string;
}

export interface ChatSessionPublic {
  id: number;
  title: string | null;
  created_at: string;
}

export interface ChatMessagePublic {
  id: number;
  role: string;
  content: Record<string, unknown>;
  created_at: string;
}

export interface ModeStatusResponse {
  mode: Mode;
  changed_at: string;
  kill_switch_at: string | null;
  live_confirmation_count: number;
}

// --- endpoint groups ---

export const auth = {
  login: (email: string, password: string) =>
    request<MeResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/auth/me'),
};

export const market = {
  quotes: (symbols: string[]) =>
    request<QuoteBatch>(`/market/quotes?symbols=${encodeURIComponent(symbols.join(','))}`),
  history: (symbol: string, period = '1m', interval = '1d') =>
    request<HistoryResponse>(
      `/market/history/${encodeURIComponent(symbol)}?period=${period}&interval=${interval}`,
    ),
  movers: (direction: 'up' | 'down' = 'up', limit = 10) =>
    request<{ direction: string; movers: { symbol: string; last: number; change_pct: number }[] }>(
      `/market/movers?direction=${direction}&limit=${limit}`,
    ),
};

export interface SyncResult {
  positions_count: number;
  equity: number;
  cash: number;
  source: 'schwab' | 'sim';
  refreshed: boolean;
}

export interface CorrelationViolation {
  symbol_a: string;
  symbol_b: string;
  correlation: number;
  thesis_a_id: string | null;
  thesis_b_id: string | null;
}

export interface CorrelationsResponse {
  matrix: Record<string, Record<string, number>>;
  violations: CorrelationViolation[];
  thesis_set_count: number;
}

export interface ThesisPerfPoint {
  date: string;
  nav: number;
  return_pct: number;
  benchmark_return_pct: number | null;
}

export interface ThesisPerfResponse {
  thesis_id: string;
  period: string;
  points: ThesisPerfPoint[];
}

// v0.5.4 AE-053 / AE-062: server-truthful connection status for the
// "Schwab disconnected" banner. Three states:
//   - "schwab"               : SchwabReadBroker live; numbers are real
//   - "schwab_unavailable"   : tokens exist but refresh failed
//   - "sim"                  : no Schwab configured; SimBroker is by design
export interface ConnectionStatus {
  status: 'schwab' | 'schwab_unavailable' | 'sim';
  schwab_connected: boolean;
  error: string | null;
}

export const portfolio = {
  account: (mode: 'combined' | 'sim' | 'live' = 'live') =>
    request<AccountPublic>(`/portfolio/account?mode=${mode}`),
  // v0.5.5 dual-ledger: returns SIM + LIVE summaries side-by-side. The
  // Portfolio header renders two NAV cards from this; Morning Brief
  // surfaces both totals.
  ledgers: () => request<DualLedgerAccount>('/portfolio/ledgers'),
  positions: (mode: 'combined' | 'sim' | 'live' = 'combined') =>
    request<PositionPublic[]>(`/portfolio/positions?mode=${mode}`),
  pnl: (period: string = '1d') => request<PnLResponse>(`/portfolio/pnl?period=${period}`),
  // v0.5.8 AE-104: daily "you vs S&P" alpha series from benchmark_snapshots
  alphaHistory: (days = 90) =>
    request<AlphaHistoryResponse>(`/portfolio/alpha-history?days=${days}`),
  sync: () => request<SyncResult>('/portfolio/sync', { method: 'POST' }),
  correlations: () => request<CorrelationsResponse>('/portfolio/correlations?scope=thesis_sets'),
  connection: () => request<ConnectionStatus>('/portfolio/connection'),
  thesisPerformance: (thesisId: string, period: string = 'all') =>
    request<ThesisPerfResponse>(`/engineer/theses/${thesisId}/performance?period=${period}`),
};

export const orders = {
  list: (status?: string) =>
    request<{ orders: OrderPublic[] }>(`/orders${status ? `?status=${status}` : ''}`),
  pending: () =>
    request<{ orders: OrderPublic[]; count: number }>('/orders/pending'),
  preview: (body: PlaceOrderRequest) =>
    request<OrderPreview>('/orders/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  place: (body: PlaceOrderRequest, liveAck = false) =>
    request<OrderPublic>('/orders', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: liveAck ? { 'X-Meeri-Live-Ack': 'true' } : undefined,
    }),
  approve: (clientOrderId: string, liveAck = false) =>
    request<{ order: OrderPublic; auto: boolean }>(
      `/orders/${encodeURIComponent(clientOrderId)}/approve`,
      {
        method: 'POST',
        headers: liveAck ? { 'X-Meeri-Live-Ack': 'true' } : undefined,
      },
    ),
  reject: (clientOrderId: string, reason?: string) =>
    request<OrderPublic>(`/orders/${encodeURIComponent(clientOrderId)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  cancel: (clientOrderId: string) =>
    request<{ canceled: boolean; client_order_id: string }>(
      `/orders/${encodeURIComponent(clientOrderId)}`,
      { method: 'DELETE' },
    ),
  // v0.5.4 AE-071: server-side defer + batch-approve. Replaces the
  // client-only fictions in MorningBriefPane.
  defer: (clientOrderId: string, hours = 24) =>
    request<OrderPublic>(`/orders/${encodeURIComponent(clientOrderId)}/defer`, {
      method: 'POST',
      body: JSON.stringify({ hours }),
    }),
  // v0.5.6 AE-036: poll the broker for working LIVE orders and promote
  // them to their real state (filled/partial/canceled). No-op in SIM.
  refresh: () =>
    request<{ checked: number; filled: number; updated: number }>(
      '/orders/refresh',
      { method: 'POST' },
    ),
  batchApprove: (clientOrderIds: string[], liveAck = false) =>
    request<{
      total: number;
      ok: number;
      fail: number;
      results: Record<string, { ok: boolean; order?: OrderPublic; status_code?: number; detail?: unknown }>;
    }>('/orders/batch-approve', {
      method: 'POST',
      body: JSON.stringify({ client_order_ids: clientOrderIds }),
      headers: liveAck ? { 'X-Meeri-Live-Ack': 'true' } : undefined,
    }),
};

export const watchlists = {
  list: () => request<{ watchlists: WatchlistPublic[] }>('/watchlists'),
  create: (name: string, symbols: string[] = []) =>
    request<WatchlistPublic>('/watchlists', {
      method: 'POST',
      body: JSON.stringify({ name, symbols }),
    }),
  addSymbol: (id: number, symbol: string) =>
    request<WatchlistPublic>(`/watchlists/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ symbol }),
    }),
  removeSymbol: (id: number, symbol: string) =>
    request<WatchlistPublic>(`/watchlists/${id}/items/${encodeURIComponent(symbol)}`, {
      method: 'DELETE',
    }),
  setDefault: (id: number) =>
    request<WatchlistPublic>(`/watchlists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_default: true }),
    }),
  remove: (id: number) =>
    request<{ ok: boolean }>(`/watchlists/${id}`, { method: 'DELETE' }),
};

export const strategies = {
  list: () =>
    request<{
      champion: { name: string; basket: string[] } | null;
      challenger: { name: string; basket: string[] } | null;
      others: unknown[];
    }>('/strategies'),
  compare: (days: number, capital = 100_000) =>
    request<{
      days: number;
      capital: number;
      champion: { name: string; metrics: Record<string, unknown>; equity_curve: number[] };
      challenger: { name: string; metrics: Record<string, unknown>; equity_curve: number[] };
      promotion_gate: { promotable: boolean; violations: string[] };
    }>('/strategies/compare', {
      method: 'POST',
      body: JSON.stringify({ days, capital }),
    }),
  promote: (candidate: string, days = 30) =>
    request<{ promoted: boolean; new_champion: string; pr_url: string | null; reason: string | null }>(
      `/strategies/promote/${candidate}?days=${days}`,
      { method: 'POST' },
    ),
};

export const chat = {
  listSessions: () => request<ChatSessionPublic[]>('/chat/sessions'),
  createSession: (title?: string) =>
    request<ChatSessionPublic>('/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ title: title ?? null }),
    }),
  listMessages: (id: number) => request<ChatMessagePublic[]>(`/chat/sessions/${id}/messages`),
  deleteSession: (id: number) =>
    request<{ ok: boolean }>(`/chat/sessions/${id}`, { method: 'DELETE' }),
};

export interface RiskSettings {
  max_trade_usd: number | null;
  max_daily_drawdown_pct: number | null;
  instrument_allowlist: string | null;
}

export interface ApprovalPolicy {
  auto_approve_sim: boolean;
  auto_approve_live_under_usd: number | null;
  daily_live_usd_limit: number | null;
  weekly_live_usd_limit: number | null;
  monthly_live_usd_limit: number | null;
  approval_expiry_seconds_market: number;
  approval_expiry_days_limit: number;
  aggregates: { daily: number; weekly: number; monthly: number };
}

export interface ApprovalPolicyUpdate {
  password?: string;
  auto_approve_sim?: boolean;
  auto_approve_live_under_usd?: number | null;
  daily_live_usd_limit?: number | null;
  weekly_live_usd_limit?: number | null;
  monthly_live_usd_limit?: number | null;
  approval_expiry_seconds_market?: number;
  approval_expiry_days_limit?: number;
  clear?: string[];
}

export interface LLMSettings {
  provider: string;
  model: string | null;
  api_key_set: boolean;
}

export interface SchwabStatus {
  connected: boolean;
  has_credentials: boolean;
  has_tokens: boolean;
  account_id: string | null;
  access_expires_at: string | null;
  refresh_expires_at: string | null;
}

export interface UserPreferences {
  interaction_style: 'socratic' | 'balanced' | 'agentic';
  risk_tolerance: number;
  time_horizon: 'intraday' | 'swing' | 'long';
  themes: string[];
  min_conviction_to_propose: number;
  note: string | null;
  market_signal_mode: 'off' | 'extremes' | 'always';
  updated_at: string;
}

export interface ButlerProfile {
  user_id: number;
  interaction_style: string;
  risk_tolerance: number;
  time_horizon: string;
  themes_declared: string[];
  themes_observed: string[];
  min_conviction_to_propose: number;
  note: string | null;
  recent_kinds: Record<string, number>;
  recent_intents: Record<string, number>;
  recent_outcomes: Record<string, number>;
  recent_facts: { kind: string; intent: string | null; outcome: string | null; summary: string | null; ts: string }[];
  captured_at: number;
}

export const me = {
  getPreferences: () => request<UserPreferences>('/me/preferences'),
  setPreferences: (body: Partial<UserPreferences>) =>
    request<UserPreferences>('/me/preferences', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getProfile: () => request<ButlerProfile>('/me/profile'),
};

export interface ThesisResult {
  thesis: string;
  universe_seed: { theme: string; universe: string[]; theme_keywords: string[] };
  macro_backdrop: Record<string, unknown>;
  theme_momentum: Record<string, unknown>;
  fundamental_ranking: {
    n_input?: number;
    n_passed?: number;
    criteria?: Record<string, unknown>;
    results: {
      symbol: string;
      name?: string;
      score: number;
      revenue?: number;
      net_income?: number;
      operating_cash_flow?: number;
      debt_to_assets?: number | null;
    }[];
  };
  per_name_sentiment: { symbol: string; sentiment: Record<string, unknown> }[];
}

// v0.5.8 AE-105: FRED series arrays for macro charts
export interface MacroSeriesPoint {
  date: string;
  value: number;
}

export interface MacroSeriesResponse {
  days: number;
  series: Record<string, { label: string; points: MacroSeriesPoint[] }>;
}

export const research = {
  thesis: (query: string, depth = 8, criteria?: Record<string, unknown>) =>
    request<ThesisResult>('/research/thesis', {
      method: 'POST',
      body: JSON.stringify({ query, depth, criteria }),
    }),
  fundamentals: (symbol: string) =>
    request<Record<string, unknown>>(`/research/fundamentals/${encodeURIComponent(symbol)}`),
  filings: (symbol: string, limit = 8) =>
    request<{ symbol: string; n: number; filings: { form: string; date: string; url: string | null }[] }>(
      `/research/filings/${encodeURIComponent(symbol)}?limit=${limit}`,
    ),
  macro: () => request<Record<string, Record<string, unknown>>>('/research/macro'),
  // v0.5.8 AE-105: dated FRED observations for charts (allowlisted ids)
  macroSeries: (ids: string[], days = 90) =>
    request<MacroSeriesResponse>(
      `/research/macro/series?ids=${encodeURIComponent(ids.join(','))}&days=${days}`,
    ),
  news: (params: { symbol?: string; theme?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.theme) qs.set('theme', params.theme);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<{ subject: string; n: number; items: { title: string; url: string; source: string; ts: string | null; summary: string }[] }>(
      `/research/news?${qs.toString()}`,
    );
  },
  history: (limit = 20, offset = 0) =>
    request<{ sessions: { id: string; query: string; depth: number; status: string; created_at: string; updated_at: string }[]; total: number }>(
      `/research/history?limit=${limit}&offset=${offset}`,
    ),
  historyGet: (id: string) =>
    request<Record<string, unknown>>(`/research/history/${id}`),
  buyThesis: (body: {
    research_session_id?: string | null;
    symbols: string[];
    weights: number[];
    stops?: Record<string, number> | null;
    total_exposure_usd?: number | null;
    total_exposure_pct_nav?: number | null;
    // v0.5.5 AE-076: explicit mode for the Buy SIM / Buy LIVE buttons.
    // 'sim' forces SimBroker even if the user is in LIVE mode.
    // 'live' is REJECTED unless the user is in LIVE mode.
    mode?: 'sim' | 'live';
    thesis_name?: string;
    statement_md?: string;
  }) =>
    request<{
      total_exposure_usd?: number;
      orders_placed: unknown[];
      orders_pending: unknown[];
      thesis_id?: string | null;
      mode?: string;
      error?: string;
      error_detail?: string;
    }>('/research/buy-thesis', { method: 'POST', body: JSON.stringify(body) }),
  exposureRecommendation: () =>
    request<{ recommended_usd: number | null; recommended_pct_nav: number | null; nav_usd?: number }>('/research/exposure-recommendation'),
  optimizeAllocation: (body: {
    symbols: string[];
    total_exposure_usd: number;
    method?: string;
  }) =>
    request<{
      weights: Record<string, number>;
      shares: Record<string, number>;
      expected_sharpe: number | null;
      expected_annual_return_pct: number | null;
      expected_annual_vol_pct: number | null;
      correlation_with_portfolio: number | null;
      diversification_benefit: string;
      flags: string[];
      reasoning: string;
      method: string;
    }>('/research/optimize-allocation', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  correlationMatrix: (symbols: string[]) =>
    request<{
      symbols: string[];
      existing_symbols: string[];
      thesis_internal: Record<string, Record<string, number>>;
      thesis_vs_existing: Record<string, Record<string, number>>;
      high_correlation_flags: string[];
    }>('/research/correlation-matrix', {
      method: 'POST',
      body: JSON.stringify({ symbols }),
    }),
  atrStops: (symbols: string[], multiplier = 2.0) =>
    request<{ stops: { symbol: string; atr_14d: number | null; atr_pct: number | null; recommended_stop_pct: number; last_price: number | null; stop_price: number | null; rationale: string }[] }>(
      '/research/atr-stops',
      { method: 'POST', body: JSON.stringify({ symbols, multiplier }) },
    ),
  scenarioSize: (body: { symbol: string; last_price: number; max_loss_usd: number; target_gain_pct: number; stop_pct: number }) =>
    request<{ symbol: string; shares: number; dollar_amount: number; max_loss_usd: number; target_gain_usd: number; stop_pct: number; reward_risk_ratio: number; reasoning: string }>(
      '/research/scenario-size',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /**
   * AE-010 (UX-WIZARD-01): support/resistance + entry-zone classification
   * for a symbol. Returns `{ available: false, reason }` when the broker
   * cannot supply enough bars — anti-hallucination Layer 4 (never
   * fabricate a zone).
   */
  technicalZones: (body: { symbol: string; lookback?: number; method?: string }) =>
    request<TechnicalZonesResponse>(
      '/research/technical-zones',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  whatChanged: (symbols: string[], saved_prices: Record<string, number>) =>
    request<{ changes: { symbol: string; price_change_pct: number | null; momentum_shift: string | null; notable: boolean }[] }>(
      '/research/what-changed',
      { method: 'POST', body: JSON.stringify({ symbols, saved_prices }) },
    ),
  thesisPerformance: () =>
    request<{ trackers: { id: string; thesis_name: string; total_invested_usd: number; entry_date: string; current_value_usd: number | null; return_pct: number | null }[] }>(
      '/research/thesis-performance',
    ),
};

export const settings = {
  modeStatus: () => request<ModeStatusResponse>('/settings/mode'),
  changeMode: (target: Mode, password: string, ack = false) =>
    request<ModeStatusResponse>('/settings/mode', {
      method: 'POST',
      body: JSON.stringify({
        target_mode: target,
        password,
        acknowledge_real_money: ack,
      }),
    }),
  killSwitch: () =>
    request<{ ok: boolean }>('/settings/kill-switch', { method: 'POST' }),
  resetSim: () =>
    request<{
      sim_orders_deleted: number;
      wizard_drafts_deleted: number;
      sim_ledger_reset?: boolean;
      live_data_preserved: boolean;
    }>('/settings/reset-sim', { method: 'POST' }),
  getRisk: () => request<RiskSettings>('/settings/risk'),
  setRisk: (body: RiskSettings) =>
    request<RiskSettings>('/settings/risk', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getLLM: () => request<LLMSettings>('/settings/llm'),
  setLLM: (body: { provider?: string; model?: string; api_key?: string; clear_api_key?: boolean }) =>
    request<LLMSettings>('/settings/llm', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getApproval: () => request<ApprovalPolicy>('/settings/approval'),
  setApproval: (body: ApprovalPolicyUpdate) =>
    request<ApprovalPolicy>('/settings/approval', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export interface SchwabSyncResult {
  ok: boolean;
  positions: Record<string, unknown> | null;
  transactions_parsed: number;
  transactions_error: string | null;
  lots: { refined: string[]; skipped: Record<string, string> } | null;
  detail: string | null;
}

export const schwab = {
  status: () => request<SchwabStatus>('/schwab/status'),
  setCredentials: (app_key: string, app_secret: string) =>
    request<{ ok: boolean }>('/schwab/credentials', {
      method: 'POST',
      body: JSON.stringify({ app_key, app_secret }),
    }),
  authorizeUrl: () => request<{ url: string }>('/schwab/authorize-url'),
  testConnection: () =>
    request<Record<string, unknown>>('/schwab/test-connection', { method: 'POST' }),
  disconnect: () =>
    request<{ ok: boolean }>('/schwab/disconnect', { method: 'POST' }),
  selectAccount: (account_id: string) =>
    request<{ ok: boolean }>('/schwab/select-account', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    }),
  // v0.5.6 AE-086: manual positions + transaction-lot sync.
  sync: () => request<SchwabSyncResult>('/schwab/sync', { method: 'POST' }),
};

// --- v0.5.6 AE-088: tax-return ingestion ---

export interface TaxField {
  value: number;
  source_line: string;
  form: string;
}

export interface TaxProfile {
  id: number;
  tax_year: number;
  entity: 'personal' | 's_corp';
  entity_name: string | null;
  filing_status: string | null;
  state: string | null;
  fields: Record<string, TaxField>;
  documents: { filename: string; form_type: string; uploaded_at: string }[];
  status: 'pending_confirmation' | 'confirmed';
  updated_at: string | null;
}

export interface TaxUploadResult {
  profiles: TaxProfile[];
  parsed: Record<string, unknown>[];
  errors: { filename: string; error: string }[];
}

export interface TaxLot {
  lot_id: string;
  symbol: string;
  qty: number;
  open_date: string;
  held_days: number;
  term: 'short' | 'long';
  lt_on: string | null;
  cost_basis: number;
  cost_per_share: number | null;
  source: string;
  last_price: number | null;
  market_value: number | null;
  unrealized: number | null;
  unrealized_pct: number | null;
}

export interface TaxUnrealized {
  as_of: string;
  lots: TaxLot[];
  totals: {
    short_unrealized: number;
    long_unrealized: number;
    unpriced_lots: number;
    lot_count: number;
  };
}

export interface TlhCandidate extends TaxLot {
  loss: number;
  marginal_rate_applied: number;
  est_tax_savings: number;
  wash_sale_risk: boolean;
  recent_buys: { client_order_id: string; filled_at: string; quantity: number; mode: string }[];
  no_rebuy_before: string;
}

export interface TaxTlh {
  as_of: string;
  candidates: TlhCandidate[];
  summary: {
    candidate_count: number;
    wash_sale_flagged: number;
    harvestable_loss: number;
    est_tax_savings: number;
    carryover_st: number;
    carryover_lt: number;
    rates_source: string;
  };
  thresholds: { min_loss_usd: number; min_loss_pct: number };
  assumptions: string[];
}

export interface TaxRates {
  source: string;
  tax_year: number;
  filing_status: string;
  short_term_combined: number;
  long_term_combined: number;
  assumptions: string[];
  situation: {
    profile_id: number | null;
    filing_status: string;
    state: string;
    taxable_income: number;
    carryover_st: number;
    carryover_lt: number;
    sources: Record<string, string>;
    warnings: string[];
  };
}

export const tax = {
  uploadReturns: (files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    return request<TaxUploadResult>('/tax/returns/upload', {
      method: 'POST',
      body: form,
    });
  },
  confirm: (profileId: number, corrections?: Record<string, number>) =>
    request<TaxProfile>(`/tax/returns/${profileId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ corrections: corrections ?? null }),
    }),
  profiles: () => request<{ profiles: TaxProfile[] }>('/tax/profile'),
  unrealized: () => request<TaxUnrealized>('/tax/unrealized'),
  tlh: () => request<TaxTlh>('/tax/tlh'),
  rates: () => request<TaxRates>('/tax/rates'),
};

// ---------------------------------------------------------------------------
// v0.5.6 Morning bulletin (AE-093) — agent cards + action items
// ---------------------------------------------------------------------------

export interface BulletinCard {
  id: string;
  title: string;
  available: boolean;
  data: Record<string, unknown> | null;
  error: string | null;
}

export interface BulletinActionItem {
  kind: string;
  severity: string;
  text: string;
  /** v0.5.7 AE-097: route to the surface where the action can be taken. */
  deeplink?: string | null;
}

export interface Bulletin {
  day: string;
  generated_at: string;
  version: number;
  source: string;
  cards: BulletinCard[];
  action_items: BulletinActionItem[];
}

export interface BulletinStatus {
  exists: boolean;
  day: string;
  bulletin: Bulletin | null;
}

export const bulletin = {
  today: () => request<BulletinStatus>('/bulletin/today'),
  latest: () => request<BulletinStatus>('/bulletin/latest'),
  generate: () => request<Bulletin>('/bulletin/generate', { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// v0.5.6 Calibration ledger (AE-030) — Brier / hit-rate read surface
// ---------------------------------------------------------------------------

export interface CalibrationSnapshot {
  window_days: number;
  entries_count: number;
  brier_score_overall: number | null;
  brier_by_conviction_bucket: Record<string, number>;
  scenario_hit_rate: number | null;
  track_attribution: Record<string, number>;
  conviction_calibration: Record<string, Record<string, number>>;
  drift_alerts: string[];
  generated_at: string;
  brief_summary: string;
}

export interface CalibrationEntry {
  id: string;
  decision_card_id: string;
  subject: string;
  conviction: number;
  scenarios: Record<string, unknown>[];
  actual_return_pct: number;
  outcome_date: string;
  holding_days: number;
  track_attributions: Record<string, number>;
  created_at: string;
}

export interface CalibrationLedger {
  snapshot: CalibrationSnapshot;
  entries: CalibrationEntry[];
}

// ---------------------------------------------------------------------------
// v0.5.7 AE-099 — strategy library recommendations (read surface; deciding
// a recommendation happens through orders.approve / orders.reject)
// ---------------------------------------------------------------------------

export interface StrategyManifest {
  id: string;
  name: string;
  description: string;
  signal_inputs: string[];
  universe: string;
  sizing: string;
  version: string;
}

export interface Recommendation {
  id: string;
  strategy_id: string;
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  est_price: number | null;
  rationale: string;
  confidence: number;
  signal_inputs: Record<string, unknown>;
  client_order_id: string | null;
  pair_group: string | null;
  mode: string;
  day: string;
  order_status: string | null;
  scored_at: string | null;
  outcome_return_pct: number | null;
  created_at: string | null;
}

export const recommendations = {
  library: () => request<{ strategies: StrategyManifest[] }>('/recommendations/library'),
  list: (params: { strategy_id?: string; day?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.strategy_id) q.set('strategy_id', params.strategy_id);
    if (params.day) q.set('day', params.day);
    if (params.limit != null) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request<{ recommendations: Recommendation[] }>(
      `/recommendations${qs ? `?${qs}` : ''}`,
    );
  },
};

// v0.5.7 AE-099: per-strategy scorecard (recommendation outcomes)
export interface StrategyScorecardRow {
  strategy_id: string;
  name: string;
  proposed: number;
  order_status_counts: Record<string, number>;
  scored: number;
  hit_rate_pct: number | null;
  avg_outcome_return_pct: number | null;
  best_outcome_pct: number | null;
  worst_outcome_pct: number | null;
  // v0.5.8 AE-107: chronological per-outcome returns for the distribution strip
  outcome_returns_pct: number[];
}

export const calibration = {
  get: (windowDays = 90) =>
    request<CalibrationLedger>(`/calibration?window_days=${windowDays}`),
  strategies: () =>
    request<{ strategies: StrategyScorecardRow[] }>('/calibration/strategies'),
};

// v0.5.8 AE-103: fear & greed gauge + persisted daily history
export interface FearGreedReading {
  available: boolean;
  score: number | null;
  rating: string | null;
  source: string | null;
  approximate: boolean;
  note?: string | null;
}

export interface FearGreedHistoryPoint {
  day: string;
  score: number;
  rating: string;
  source?: string | null;
}

export interface FearGreedResponse {
  current: FearGreedReading;
  history: FearGreedHistoryPoint[];
}

export const sentiment = {
  fearGreed: (days = 30) =>
    request<FearGreedResponse>(`/sentiment/fear-greed?days=${days}`),
};

export interface TicketPublic {
  id: string;
  specialist: string;
  action: string;
  mode: string;
  confidence: number;
  desk_id: string | null;
  payload: Record<string, unknown>;
  parents: string[];
  created_at: string;
}

export const tickets = {
  list: (limit = 50) =>
    request<{ tickets: TicketPublic[] }>(`/tickets?limit=${limit}`),
};

export interface PanePublic {
  id: string;
  name: string;
  state: string;
  manifest: Record<string, unknown>;
  last_active_at: string | null;
}

export const panes = {
  list: () => request<PanePublic[]>('/panes'),
  get: (id: string) => request<PanePublic>(`/panes/${id}`),
  pause: (id: string) =>
    request<{ ok: boolean; pane_id: string; state: string }>(`/panes/${id}/pause`, {
      method: 'POST',
    }),
  resume: (id: string) =>
    request<{ ok: boolean; pane_id: string; state: string }>(`/panes/${id}/resume`, {
      method: 'POST',
    }),
  /** A2A passthrough — call a service-pane skill */
  rpc: (id: string, method: string, params: Record<string, unknown> = {}) =>
    request<unknown>(`/a2a/${id}/rpc`, {
      method: 'POST',
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
    }),
};

// ---------------------------------------------------------------------------
// v0.5.1 Morning Coffee — read-only status endpoints
// ---------------------------------------------------------------------------

export interface MosPhaseView {
  name: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
}

export interface MosHaltView {
  reason: string;
  source: string;
  halted_at: string;
  resolved_at: string | null;
  resolved_reason: string | null;
}

export interface MosStatus {
  date: string;
  current_phase: string | null;
  phases: MosPhaseView[];
  halts: MosHaltView[];
  is_halted: boolean;
  is_stale: boolean;
  digest: string;
  first_run_today: boolean;
}

export type BreakerStatus = 'ok' | 'warn' | 'tripped';

export interface BreakerVerdict {
  id: string;
  status: BreakerStatus;
  message: string;
  details: Record<string, unknown>;
}

export interface BreakersReport {
  verdicts: BreakerVerdict[];
  tripped_count: number;
  warned_count: number;
  ok_count: number;
  should_halt: boolean;
  halt_reason: string;
}

export interface AutonomyCapability {
  id: string;
  description: string;
  propose: 'auto' | 'manual';
  execute: 'auto' | 'manual';
  graduation_criteria: string | null;
}

export interface AutonomyHaltContact {
  email: string;
  on: string[];
}

export interface Autonomy {
  intent_version: string;
  meeri_version: string;
  capabilities: AutonomyCapability[];
  halt_contacts: AutonomyHaltContact[];
}

export interface PlaybookConstraintView {
  id: string;
  description: string;
  params: Record<string, unknown>;
}

export interface PlaybookCurrent {
  policy_version: string;
  intent_version: string;
  constraints: PlaybookConstraintView[];
  donts: string[];
  preface: string;
}

export const morningCoffee = {
  mosStatus: () => request<MosStatus>('/mos/status'),
  circuitBreakers: () => request<BreakersReport>('/circuit-breakers'),
  autonomy: () => request<Autonomy>('/autonomy'),
  playbookCurrent: () => request<PlaybookCurrent>('/playbook/current'),
};

// ---------------------------------------------------------------------------
// v0.5.2 Morning Brief — decision cards for the morning coffee UX
// ---------------------------------------------------------------------------

export interface BriefDecision {
  id: string;
  headline: string;
  action: string;
  reasoning: string;
  contrarian_note: string | null;
  order?: PlaceOrderRequest;
  client_order_id?: string;
  status: 'pending' | 'approved' | 'deferred' | 'debated';
}

export interface MorningBriefFull {
  date: string;
  portfolio: {
    // v0.5.4 AE-047: total_nav is nullable. Server returns null when the
    // broker read failed (Schwab unreachable, malformed payload, etc.);
    // UI must render "—" and surface the source / source_error so the
    // operator knows the data is unavailable rather than $0.
    //
    // v0.5.5 dual-ledger: total_nav is now SIM + LIVE combined when both
    // are available; sim_nav and live_nav break it down for the operator
    // who cares which half is paper.
    total_nav: number | null;
    day_change_pct: number | null;
    ytd_return_pct?: number | null;
    ytd_vs_sp500_bps: number | null;
    benchmark_available?: boolean;
    sim_nav?: number | null;
    live_nav?: number | null;
    // AE-053 surface for the UI banner.
    source?: 'schwab' | 'schwab_unavailable' | 'sim';
    source_error?: string | null;
  };
  decisions: BriefDecision[];
  playbook_status: { ok: boolean; breaches: string[] };
  deposit_preview: { amount: number; expected_date: string; allocation_summary: string } | null;
  hygiene_highlights: string[];
  mos_digest: string;
}

// Server endpoint shape (api/v1/morning_coffee/brief). Extends BriefDecision
// with thesis-context metadata so the UI can render INTENT-shaped headlines.
export interface ServerBriefDecision extends BriefDecision {
  thesis_id?: string | null;
  thesis_symbol?: string | null;
  thesis_archetype?: string | null;
  source?: string | null;
  created_at?: string | null;
  // v0.5.7 AE-099: strategy-recommendation provenance (orders queued by
  // the strategy library carry the strategy id + confidence + the pane
  // facts the proposal was computed from).
  strategy_id?: string | null;
  confidence?: number | null;
  signal_inputs?: Record<string, unknown> | null;
  pair_group?: string | null;
}

export const morningBrief = {
  // v0.5.4 AE-047: server-side `/morning_coffee/brief` is the SINGLE
  // source of truth for the Morning Brief numbers. Previously this client
  // would silently fall back to a `synthesizeBrief` stub that filled in
  // `total_nav: 0`, `day_change_pct: 0`, `ytd_vs_sp500_bps: 0` whenever
  // the server endpoint failed — violating the anti-hallucination rule
  // (AE-002) by showing fake zeros where data was actually unavailable.
  //
  // Now: server failure throws and the UI renders an "unavailable"
  // banner (MorningBriefPane already handles `!brief`). No silent
  // fabrications.
  get: (): Promise<MorningBriefFull> => request<MorningBriefFull>('/morning_coffee/brief'),
  fromMos: (_mos: MosStatus): Promise<MorningBriefFull> =>
    request<MorningBriefFull>('/morning_coffee/brief'),
};

// ---------------------------------------------------------------------------
// v0.5 Engineer mode — thesis wizard + research (SIM-only surface)
// ---------------------------------------------------------------------------

export interface WizardRankedOption {
  driver: string;
  label: string;
  diversification_score: number;
  rationale: string;
}

export interface WizardStep1View {
  selected_driver: string | null;
  ranked_options: WizardRankedOption[];
}

export interface WizardStep2View {
  raw_statement: string;
  polished_statement: string;
  success_criteria: string[];
  failure_criteria: string[];
  invalidation_triggers: string[];
  horizon_days: number;
  archetype: string | null;
}

export interface WizardStep3View {
  target_satellite_weight_pct: number;
  blended_cagr_target: number;
  existing_contribution_cagr: number;
  satellite_weight_of_nav: number;
  required_cagr_value: number;
  historical_p95_cagr: number;
  verdict: string | null;
  verdict_explanation: string;
}

// v0.5.9 AE-112: shape of a desk-suggested candidate stored by
// POST .../step4/suggest (evidence comes from the fundamentals screen;
// sentiment_score from the sentiment desk; null when unavailable).
export interface WizardCandidate {
  symbol: string;
  rank: number;
  evidence: Record<string, unknown>;
  sentiment_score: number | null;
  source: string;
}

export interface WizardStep4View {
  candidates: Array<Record<string, unknown>>;
  correlation_matrix: Record<string, Record<string, number>>;
  selected_symbols: string[];
}

export interface WizardStep5View {
  contrarian_review_md: string;
  reviewer_note: string;
  commit_approved: boolean;
  /** v0.5.9 AE-112: the Contrarian PANE's side-by-side take (reference only). */
  pane_take_md: string;
}

export interface WizardState {
  wizard_id: string;
  user_id: number;
  current_step: number;
  step1: WizardStep1View;
  step2: WizardStep2View;
  step3: WizardStep3View;
  step4: WizardStep4View;
  step5: WizardStep5View;
  created_at: string;
  updated_at: string;
}

export interface WizardCommitResponse {
  thesis_id: string;
  action: string;
  positions_committed: number;
  pending_orders_queued: number;
  message: string;
}

export interface EngineerThesis {
  id: string;
  symbol: string | null;
  archetype: string;
  thesis_type: string;
  p_success: number;
  q_failure: number;
  target: number | null;
  stop: number | null;
  conviction: number;
  horizon_days: number;
  status: string;
  intent_version: string;
  positions: Array<{ symbol: string; target_weight_pct: number; target_qty: number | null }>;
}

// v0.5.5 AE-079 — rich thesis detail surfaced by GET /engineer/theses/{id}.
export interface AttributionRow {
  symbol: string;
  mode: 'sim' | 'live' | string;
  attributed_qty: number;
  attributed_value: number;
  price?: number | null;
  weight_in_thesis?: number | null;
}

export interface AttributionSummary {
  sim_value: number;
  live_value: number;
  total_value: number;
  rows: AttributionRow[];
}

export interface ThesisDetail {
  id: string;
  symbol: string | null;
  statement_md: string;
  archetype: string;
  thesis_type: string;
  p_success: number;
  q_failure: number;
  target: number | null;
  stop: number | null;
  invalidation_trigger: string | null;
  horizon_days: number;
  conviction: number;
  status: string;
  contrarian_review_md: string | null;
  entry_date: string | null;
  last_review_at: string | null;
  intent_version: string;
  positions: Array<{
    symbol: string;
    target_weight_pct: number;
    target_qty: number | null;
    added_at?: string | null;
    source?: string | null;
  }>;
  attribution: AttributionSummary;
}

export interface DriversCatalog {
  drivers: Array<{ driver: string; label: string }>;
}

export const engineer = {
  // Wizard endpoints
  wizardStart: (body: { existing_coverage?: Record<string, number>; top_k_uncovered?: number }) =>
    request<WizardState>('/engineer/theses/wizard/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  wizardGet: (wizardId: string) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}`),
  wizardStep1: (wizardId: string, driver: string) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step1`, {
      method: 'POST',
      body: JSON.stringify({ driver }),
    }),
  wizardStep2: (
    wizardId: string,
    body: {
      polished_statement: string;
      success_criteria: string[];
      failure_criteria: string[];
      invalidation_triggers: string[];
      horizon_days: number;
      archetype: string;
    },
  ) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step2`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  wizardStep3: (
    wizardId: string,
    body: {
      target_satellite_weight_pct: number;
      blended_cagr_target: number;
      existing_contribution_cagr: number;
      satellite_weight_of_nav?: number;
      symbol?: string;
    },
  ) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step3`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  wizardStep4: (
    wizardId: string,
    body: {
      selected_symbols: string[];
      correlation_matrix?: Record<string, Record<string, number>>;
      correlation_cap?: number;
    },
  ) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step4`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // v0.5.9 AE-112 — "Ask the desks": thesis pane pipeline ranks candidates
  // via real A2A dispatches; 503 when the desks are unavailable.
  wizardStep4Suggest: (wizardId: string, limit = 8) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step4/suggest`, {
      method: 'POST',
      body: JSON.stringify({ limit }),
    }),
  wizardStep5: (wizardId: string, contrarianReviewMd: string) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step5`, {
      method: 'POST',
      body: JSON.stringify({ contrarian_review_md: contrarianReviewMd }),
    }),
  // v0.5.9 AE-112 — "The Contrarian's take": side-by-side reference; the
  // user's own steelman still gates commit.
  wizardStep5Contrarian: (wizardId: string) =>
    request<WizardState>(`/engineer/theses/wizard/${wizardId}/step5/contrarian`, {
      method: 'POST',
    }),
  wizardCommit: (wizardId: string) =>
    request<WizardCommitResponse>(`/engineer/theses/wizard/${wizardId}/commit`, {
      method: 'POST',
    }),
  wizardDiscard: (wizardId: string) =>
    request<{ wizard_id: string; discarded: boolean }>(
      `/engineer/theses/wizard/${wizardId}`,
      { method: 'DELETE' },
    ),

  listTheses: () => request<{ theses: EngineerThesis[] }>('/engineer/theses'),
  // v0.5.5 AE-079 — rich thesis detail for the FocalCard thesis_detail overlay.
  getThesis: (thesisId: string) =>
    request<ThesisDetail>(`/engineer/theses/${thesisId}`),
  liquidate: (thesisId: string) =>
    request<WizardCommitResponse>(`/engineer/theses/${thesisId}/liquidate`, {
      method: 'POST',
    }),

  drivers: (topK = 13) =>
    request<DriversCatalog>(`/engineer/research/drivers?top_k=${topK}`),
  validateThesis: (body: {
    archetype: string;
    horizon_days: number;
    required_cagr: number;
    symbol?: string;
    lookback_years?: number;
  }) =>
    request<{
      matches_found: number;
      lookback_years: number;
      median_cagr_pct: number;
      p80_cagr_pct: number;
      p95_cagr_pct: number;
      worst_drawdown_pct: number;
      sharpe_median: number;
      sharpe_range: number[];
      verdict: string;
      explanation: string;
      is_stub: boolean;
    }>('/engineer/research/validate_thesis', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requiredCagr: (body: {
    target_blended_cagr: number;
    existing_contribution_cagr: number;
    new_thesis_satellite_weight: number;
    satellite_weight_of_nav?: number;
  }) =>
    request<{ required_cagr: number; required_cagr_pct: number }>(
      '/engineer/research/required_cagr',
      { method: 'POST', body: JSON.stringify(body) },
    ),
};
